/**
 * React Query hooks for /api/v1/well-servicing/* (WS-1).
 *
 * EVERY HOOK RETURNS THE META: the well identifier label and the mast-rating
 * unit come from it. EVERY KEY STARTS ['wellServicing'] and every write
 * invalidates that one prefix. EVERY READ SPREADS LOAD_FAILURE_QUERY_OPTIONS,
 * so a refusal is not retried and reads as a refusal, never as an empty list.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrapArray } from '../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import type {
  WsActivityCode,
  WsActivityCodesMeta,
  WsActivityCodeWrite,
  WsCatalog,
  WsCrew,
  WsCrewMember,
  WsCrewMemberWrite,
  WsCrewWrite,
  WsCustomerSettings,
  WsCustomerSettingsUpdate,
  WsMeta,
  WsResult,
  WsRig,
  WsRigDetail,
  WsRigWrite,
  WsSettings,
  WsSettingsUpdate,
  WsStarterResult,
  WsWell,
  WsWellWrite,
} from '../types/well-servicing';

/** No zone: the tenant's zone arrives in meta, never a literal (as useWellTesting). */
const NO_ZONE: string | null = null;

/** Meta did not arrive: no zone and no country, rather than guessed ones. */
const EMPTY_META: WsMeta = {
  unitSystem: null,
  unitSystemSource: null,
  units: null,
  currency: null,
  country: null,
  taxSystem: null,
  timeZone: NO_ZONE,
  today: null,
};

/** Split a body into payload and meta. `data: null` stays null — never the envelope. */
export function unwrapWs<T>(body: unknown): WsResult<T | null> {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return { data: null, meta: EMPTY_META };
  const b = body as { data?: unknown; meta?: Partial<WsMeta> };
  const data = 'data' in b && b.data !== undefined && b.data !== null ? (b.data as T) : null;
  return { data, meta: { ...EMPTY_META, ...(b.meta ?? {}) } };
}

/** A list read: a non-array payload becomes `[]` rather than reaching `.map`. */
export function unwrapWsList<T>(body: unknown): WsResult<T[]> {
  const { data, meta } = unwrapWs<unknown>(body);
  return { data: Array.isArray(data) ? (data as T[]) : [], meta };
}

export interface WsWellFilters {
  customerId?: string;
  fluidType?: string;
  provinceState?: string;
  q?: string;
}

export type WsCodeActive = 'true' | 'false' | 'all';

const clean = (p?: object) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) if (v) out[k] = String(v);
  return Object.keys(out).length ? out : undefined;
};

export const wsKeys = {
  all: ['wellServicing'] as const,
  meta: () => ['wellServicing', 'meta'] as const,
  settings: () => ['wellServicing', 'settings'] as const,
  customerSettings: (id: string) => ['wellServicing', 'customerSettings', id] as const,
  customers: () => ['wellServicing', 'customers'] as const,
  users: () => ['wellServicing', 'users'] as const,
  equipment: () => ['wellServicing', 'equipment'] as const,
  wells: (p?: WsWellFilters) => ['wellServicing', 'wells', clean(p) ?? {}] as const,
  well: (id: string) => ['wellServicing', 'well', id] as const,
  rigs: () => ['wellServicing', 'rigs'] as const,
  rig: (id: string) => ['wellServicing', 'rig', id] as const,
  activityCodes: (active: WsCodeActive) => ['wellServicing', 'activityCodes', active] as const,
};

export const invalidateWs = (qc: QueryClient) => qc.invalidateQueries({ queryKey: wsKeys.all });

/** Every write invalidates the whole prefix: a crew add moves the register count and the detail. */
function useWsWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateWs(qc) });
}

/** The catalog (ticket modes, tours, fluids, rig classes, positions, billing classes) with the API's labels. */
export function useWsMeta() {
  return useQuery({
    queryKey: wsKeys.meta(),
    queryFn: async () => unwrapWs<WsCatalog>((await api.wellServicing.meta()).data),
    staleTime: 5 * 60_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWsSettings() {
  return useQuery({
    queryKey: wsKeys.settings(),
    queryFn: async () => unwrapWs<WsSettings>((await api.wellServicing.settings.get()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useUpdateWsSettings = () =>
  useWsWrite(async (body: WsSettingsUpdate) => unwrapWs<WsSettings>((await api.wellServicing.settings.update(body)).data));

export function useWsCustomerSettings(customerId: string | undefined) {
  return useQuery({
    queryKey: wsKeys.customerSettings(customerId ?? ''),
    queryFn: async () => unwrapWs<WsCustomerSettings>((await api.wellServicing.customerSettings.get(customerId as string)).data),
    enabled: !!customerId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useUpdateWsCustomerSettings = () =>
  useWsWrite(async ({ customerId, body }: { customerId: string; body: WsCustomerSettingsUpdate }) =>
    unwrapWs<WsCustomerSettings>((await api.wellServicing.customerSettings.update(customerId, body)).data));

/** The tenant's customers, for the well and customer-settings pickers. */
export function useWsCustomers(enabled = true) {
  return useQuery({
    queryKey: wsKeys.customers(),
    queryFn: async () =>
      unwrapArray<{ id: string; name?: string | null; companyName?: string | null }>((await api.customers.list()).data)
        .map((c) => ({ id: c.id, name: c.name || c.companyName || '—' }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The tenant's people (not terminated, not customer logins), for the crew picker. */
export function useWsUsers(enabled = true) {
  return useQuery({
    queryKey: wsKeys.users(),
    queryFn: async () =>
      unwrapArray<{ id: string; firstName?: string; lastName?: string; fullName?: string; role?: string;
        first_name?: string; last_name?: string; employmentStatus?: string; employment_status?: string }>((await api.users.list()).data)
        .filter((u) => (u.employmentStatus ?? u.employment_status) !== 'terminated' && u.role !== 'customer')
        .map((u) => ({
          id: u.id,
          name: (u.fullName || [u.firstName ?? u.first_name, u.lastName ?? u.last_name].filter(Boolean).join(' ')).trim() || '—',
          role: u.role ?? null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The tenant's owned equipment (not retired), for the rig's equipment picker. */
export function useWsEquipmentOptions(enabled = true) {
  return useQuery({
    queryKey: wsKeys.equipment(),
    queryFn: async () =>
      unwrapArray<{ id: string; name?: string | null; unitNumber?: string | null; unit_number?: string | null;
        serialNumber?: string | null; serial_number?: string | null; status?: string | null }>((await api.equipment.list()).data)
        .filter((e) => e.status !== 'retired')
        .map((e) => ({
          id: e.id,
          name: e.name || '—',
          unitNumber: e.unitNumber ?? e.unit_number ?? null,
          serialNumber: e.serialNumber ?? e.serial_number ?? null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

// ─── Wells ───────────────────────────────────────────────────────────────────

export function useWsWells(filters?: WsWellFilters) {
  return useQuery({
    queryKey: wsKeys.wells(filters),
    queryFn: async () => unwrapWsList<WsWell>((await api.wellServicing.wells.list(clean(filters))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWsWell(id: string | undefined) {
  return useQuery({
    queryKey: wsKeys.well(id ?? ''),
    queryFn: async () => unwrapWs<WsWell>((await api.wellServicing.wells.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWsWell = () =>
  useWsWrite(async (body: WsWellWrite) => unwrapWs<WsWell>((await api.wellServicing.wells.create(body)).data));
export const useUpdateWsWell = () =>
  useWsWrite(async ({ id, body }: { id: string; body: WsWellWrite }) =>
    unwrapWs<WsWell>((await api.wellServicing.wells.update(id, body)).data));
export const useDeleteWsWell = () => useWsWrite(async (id: string) => (await api.wellServicing.wells.remove(id)).data);

// ─── Rigs, crews, members ────────────────────────────────────────────────────

export function useWsRigs() {
  return useQuery({
    queryKey: wsKeys.rigs(),
    queryFn: async () => unwrapWsList<WsRig>((await api.wellServicing.rigs.list()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWsRig(id: string | undefined) {
  return useQuery({
    queryKey: wsKeys.rig(id ?? ''),
    queryFn: async () => unwrapWs<WsRigDetail>((await api.wellServicing.rigs.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWsRig = () =>
  useWsWrite(async (body: WsRigWrite) => unwrapWs<WsRig>((await api.wellServicing.rigs.create(body)).data));
export const useUpdateWsRig = () =>
  useWsWrite(async ({ id, body }: { id: string; body: WsRigWrite }) =>
    unwrapWs<WsRig>((await api.wellServicing.rigs.update(id, body)).data));
export const useDeleteWsRig = () => useWsWrite(async (id: string) => (await api.wellServicing.rigs.remove(id)).data);

export const useCreateWsCrew = () =>
  useWsWrite(async ({ rigId, body }: { rigId: string; body: WsCrewWrite }) =>
    unwrapWs<WsCrew>((await api.wellServicing.crews.create(rigId, body)).data));
export const useUpdateWsCrew = () =>
  useWsWrite(async ({ id, body }: { id: string; body: WsCrewWrite }) =>
    unwrapWs<WsCrew>((await api.wellServicing.crews.update(id, body)).data));
export const useDeleteWsCrew = () => useWsWrite(async (id: string) => (await api.wellServicing.crews.remove(id)).data);

export const useAddWsCrewMember = () =>
  useWsWrite(async ({ crewId, body }: { crewId: string; body: WsCrewMemberWrite }) =>
    unwrapWs<WsCrewMember>((await api.wellServicing.crewMembers.add(crewId, body)).data));
export const useUpdateWsCrewMember = () =>
  useWsWrite(async ({ id, body }: { id: string; body: WsCrewMemberWrite }) =>
    unwrapWs<WsCrewMember>((await api.wellServicing.crewMembers.update(id, body)).data));
export const useRemoveWsCrewMember = () =>
  useWsWrite(async (id: string) => (await api.wellServicing.crewMembers.remove(id)).data);

// ─── Activity codes ──────────────────────────────────────────────────────────

/** The list carries its OWN meta ({ total, starterMissing }), not the tenant meta. */
export function useWsActivityCodes(active: WsCodeActive = 'all') {
  return useQuery({
    queryKey: wsKeys.activityCodes(active),
    queryFn: async () => {
      const body = (await api.wellServicing.activityCodes.list({ active })).data as unknown;
      const b = body && typeof body === 'object' && !Array.isArray(body) ? (body as { data?: unknown; meta?: Partial<WsActivityCodesMeta> }) : {};
      return {
        data: Array.isArray(b.data) ? (b.data as WsActivityCode[]) : [],
        meta: {
          total: typeof b.meta?.total === 'number' ? b.meta.total : null,
          starterMissing: typeof b.meta?.starterMissing === 'number' ? b.meta.starterMissing : null,
        } as WsActivityCodesMeta,
      };
    },
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWsActivityCode = () =>
  useWsWrite(async (body: WsActivityCodeWrite) => unwrapWs<WsActivityCode>((await api.wellServicing.activityCodes.create(body)).data));
export const useUpdateWsActivityCode = () =>
  useWsWrite(async ({ id, body }: { id: string; body: WsActivityCodeWrite }) =>
    unwrapWs<WsActivityCode>((await api.wellServicing.activityCodes.update(id, body)).data));
export const useAddWsStarterCodes = () =>
  useWsWrite(async () => unwrapWs<WsStarterResult>((await api.wellServicing.activityCodes.starter()).data));
